import {
  Flex,
  Stack,
  Grid,
  Box,
  Text,
  Button,
  useDisclosure,
  useTheme,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
} from '@chakra-ui/react'
import { addDays } from 'date-fns'
import { useState } from 'react'

import DeleteStreamAlert from 'components/delete-stream-alert'
import Heading from 'components/heading'
import { useUserContext } from 'contexts/user-context'
import igStore1 from 'assets/ig_store_1.webp'
import manageIPL from 'assets/manage_ipl.png'
import style1 from 'assets/style1.jpeg'
import style2 from 'assets/style2.jpeg'
// @ts-expect-error
import videoFile from 'assets/campaign-video.mov'

import { useGetCustomerStreams } from 'api'
import { DEFAULT_GRADE_SELECT, MAX_WEEKS_DISPLAY_IN_DAYS, STARTING_DATE } from 'settings'

import type { Stream } from 'types'

export default function ManageVideo() {
  const theme = useTheme()
  const { selectedCustomer } = useUserContext()
  const realmId = selectedCustomer?.realmID ?? 4
  const { isOpen: deleteModalIsOpen, onOpen: deleteModalOnOpen, onClose: deleteModalOnClose } = useDisclosure()
  const { isOpen: videoModalIsOpen, onOpen: videoModalOnOpen, onClose: videoModalOnClose } = useDisclosure()
  const [isProcessing, setIsProcessing] = useState(false)
  const [streamDeleting, setStreamDeleting] = useState<Stream | null>(null)
  const [activeGrade, setActiveGrade] = useState(DEFAULT_GRADE_SELECT(realmId))
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null)

  const queryPayload = {
    customerId: selectedCustomer?.customerGuid ?? '',
    startDate: addDays(STARTING_DATE, -1).toISOString(),
    endDate: addDays(STARTING_DATE, MAX_WEEKS_DISPLAY_IN_DAYS).toISOString(),
    alt: 'manage',
  }

  const streamsResult = useGetCustomerStreams(queryPayload)
  const { data: customerStreams = [] } = streamsResult

  // Fake videos data with placeholder JPEGs for now
  const fakeVideos = [
    { id: '0', title: 'Processing', date: '~3 min', thumbnail: igStore1 },
    { id: '1', title: 'IPL Campaign', date: '2024-11-11', thumbnail: manageIPL },
    { id: '2', title: 'Advertising 1', date: '2024-11-02', thumbnail: style1 },
    { id: '3', title: 'Advertising 2', date: '2024-11-03', thumbnail: style2 },
  ]

  const openVideoModal = (videoSrc: string) => {
    setSelectedVideo(videoSrc)
    videoModalOnOpen()
  }

  return (
    <>
      <Flex w="100%" h="100%" mb={8} direction="column" align="center">
        <Stack
          w="100%"
          direction="column"
          align="center"
          spacing={4}
          mx={{ base: 0, xl: 16 }}
          maxW={{ base: '100%', xl: '860px' }}
        >
          <Flex
            w="100%"
            borderRadius="5px"
            px={{ base: 4, lg: 0 }}
            py={{ base: 4, lg: 2 }}
            backgroundColor={theme.baseColors.background.default}
          >
            <Heading as="h1" text="Manage Video" />
          </Flex>

          {/* Video Thumbnails in Tiled Layout */}
          <Grid templateColumns="repeat(auto-fill, minmax(250px, 1fr))" gap={4} w="100%" py={4}>
            {fakeVideos.map(video => (
              <Box
                key={video.id}
                position="relative"
                h="200px"
                borderRadius="8px"
                overflow="hidden"
                bg="gray.200"
                boxShadow="md"
                backgroundImage={`url(${video.thumbnail})`}
                backgroundSize="cover"
                backgroundPosition="center"
              >
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  color="white"
                  bg="blackAlpha.600"
                  p={4}
                >
                  <Text fontSize="lg" fontWeight="bold" mb={2}>
                    {video.title}
                  </Text>
                  <Text fontSize="sm" mb={4}>
                    {video.date}
                  </Text>
                  {video.title !== 'Processing' && (
                    <Flex gap={2}>
                      <Button
                        colorScheme="purple"
                        onClick={() => openVideoModal(videoFile)}
                        size="sm"
                        style={video.title !== 'IPL Campaign' ? { pointerEvents: 'none' } : {}}
                      >
                        Preview
                      </Button>
                      <Button
                        as="a"
                        href={videoFile}
                        download
                        colorScheme="purple"
                        size="sm"
                        style={video.title !== 'IPL Campaign' ? { pointerEvents: 'none' } : {}}
                      >
                        Download
                      </Button>
                    </Flex>
                  )}
                </Box>
              </Box>
            ))}
          </Grid>
        </Stack>
      </Flex>

      {/* Video Modal */}
      <Modal isOpen={videoModalIsOpen} onClose={videoModalOnClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none" maxW="800px" onClick={videoModalOnClose}>
          <ModalBody display="flex" justifyContent="center" p={0}>
            {selectedVideo && (
              <video controls width="100%" onClick={(e) => e.stopPropagation()}>
                <source src={selectedVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <DeleteStreamAlert
        isOpen={deleteModalIsOpen}
        onClose={() => {
          deleteModalOnClose()
          setStreamDeleting(null)
        }}
        isLoading={isProcessing}
        setIsLoading={setIsProcessing}
        streamDeleting={streamDeleting}
        setStreamDeleting={setStreamDeleting}
        isOrphan
      />
    </>
  )
}
